import { Button } from 'blaze-ui/Button'
import { Upload5 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './BrandVisualStyleSelection.module.scss'
import cx from 'classnames'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { useState } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export interface SelectionStageProps {
  onNext: (uploadedImage?: MediaFile) => void
  onSelectImage: (image: MediaFile) => void
  onBack: () => void
  isDisabled: boolean
  brandKitId?: number
}

export const SelectionStage = ({ onNext, onSelectImage, onBack, isDisabled, brandKitId }: SelectionStageProps) => {
  const { uploadImageFilesWithoutLoadingToast, isUploading } = useImportMediaFiles(false)
  const [exampleSelected, setExampleSelected] = useState<'person' | 'product' | 'place'>('product')
  const { isMobile } = useResponsiveDesign()

  const exampleOrigins = {
    person:
      'https://res.cloudinary.com/almanac/image/upload/v1766440487/blaze_assets/virtual_marketer/onboarding/person1_el70oy.png',
    product:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/product1_mnbngo.png',
    place:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/place1_zw12wz.png',
  }

  const exampleGenerated = {
    person:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/person2_dwlv80.png',
    product:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/product2_jctec0.jpg',
    place:
      'https://res.cloudinary.com/almanac/image/upload/v1766440488/blaze_assets/virtual_marketer/onboarding/place2_txo5ma.png',
  }

  if (isMobile) {
    const leftColumnImages = [
      'https://res.cloudinary.com/almanac/image/upload/v1770666236/virtual_marketer_onboarding/7e7b3454560fe37ca8f22acc6997cd9196fdc1ac_h3ufju.png',
      'https://res.cloudinary.com/almanac/image/upload/v1770666238/virtual_marketer_onboarding/cda7b9ee47fed50e0589779c8eecd3565310445f_xx0olv.png',
      'https://res.cloudinary.com/almanac/image/upload/v1770666241/virtual_marketer_onboarding/e41c11c0a811110a89ae5fdda3f9eb3e47a30f2e_x8hgyv.png',
    ]
    const rightColumnImages = [
      'https://res.cloudinary.com/almanac/image/upload/v1770666238/virtual_marketer_onboarding/e4d291002377666050dc60c9de64c983782bc9c4_usfqjj.png',
      'https://res.cloudinary.com/almanac/image/upload/v1770666241/virtual_marketer_onboarding/eebe04e8b8b54b0afcc6c28a8eee57188737c92b_paaini.png',
      'https://res.cloudinary.com/almanac/image/upload/v1770666236/virtual_marketer_onboarding/58c1541eca3717d698d49b23eaf861114f225e10_frjquc.png',
    ]
    const centerImage =
      'https://res.cloudinary.com/almanac/image/upload/v1770666241/virtual_marketer_onboarding/eebe04e8b8b54b0afcc6c28a8eee57188737c92b_paaini.png'

    return (
      <div className={Styles.selectImageStep} data-visual-style-page="true">
        <div className={Styles.mobileTopBar} />
        <div className={Styles.mobileImageCollage}>
          <div className={Styles.leftColumn}>
            <div className={Styles.scrollingContent}>
              {[...leftColumnImages, ...leftColumnImages].map((imgSrc, index) => (
                <img
                  key={`left-${index}`}
                  src={imgSrc}
                  alt=""
                  className={Styles.columnImage}
                  onContextMenu={(e) => e?.preventDefault()}
                />
              ))}
            </div>
          </div>
          <div className={Styles.rightColumn}>
            <div className={Styles.scrollingContent}>
              {[...rightColumnImages, ...rightColumnImages].map((imgSrc, index) => (
                <img
                  key={`right-${index}`}
                  src={imgSrc}
                  alt=""
                  className={Styles.columnImage}
                  onContextMenu={(e) => e?.preventDefault()}
                />
              ))}
            </div>
          </div>
          <div className={Styles.centerImageContainer}>
            <img src={centerImage} alt="" className={Styles.centerImage} onContextMenu={(e) => e?.preventDefault()} />
            <div className={Styles.uploadedPhotoLabel}>
              <Text variant="secondary" color="var(--dark-60)">
                Uploaded photo
              </Text>
            </div>
          </div>
        </div>
        <div className={Styles.mobileGradient} />
        <div className={Styles.mobileContent}>
          <div className={Styles.mobileTextContent}>
            <Heading level={1} color="var(--light-100)">
              Start building your Brand Kit by picking a Visual Style
            </Heading>
            <Text variant="primary" color="var(--light-60)">
              Upload a photo to preview how Blaze elevates it.
            </Text>
          </div>
          <div className={Styles.mobileCTA}>
            <FileTrigger
              accept={['image/png', 'image/jpeg']}
              allowsMultiple={false}
              onSelect={async (files) => {
                if (!files) return
                const mediaFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
                  brand_kit_id: brandKitId,
                })
                const image = {
                  id: mediaFiles?.[0]?.id,
                  url: mediaFiles?.[0]?.url,
                  type: mediaFiles?.[0]?.mime_type?.startsWith('video') ? 'video' : 'image',
                }
                onSelectImage(image as MediaFile)
                onNext(image as MediaFile)
              }}
            >
              <Button
                variant="secondary"
                size="lg"
                aria-label="try it with one photo"
                isDisabled={isUploading || isDisabled}
              >
                {isUploading ? (
                  <>
                    Uploading
                    <LoadingEllipses />
                  </>
                ) : (
                  'Try it with one photo'
                )}
              </Button>
            </FileTrigger>
            {process.env.NODE_ENV === 'development' && (
              <Button variant="tertiary" size="sm" onPress={() => onNext()}>
                Skip (use defaults)
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.selectImageStep}>
      <div className={Styles.leftSide}>
        <div className={Styles.title}>
          <Heading level={1} color="var(--dark-80)">
            Next, pick a cinematic Visual Style for all your content
          </Heading>
          <div className={Styles.description}>
            <Text variant="primary" color="var(--dark-40)">
              Transform all your photos to look like they were shot by a top agency.
            </Text>
          </div>
        </div>
        <div className={Styles.content}>
          <div className={Styles.originImageContainer}>
            <img
              src={exampleOrigins[exampleSelected]}
              alt="demo origin"
              className={Styles.originImage}
              onContextMenu={(e) => e?.preventDefault()}
            />
            <div className={Styles.tryYourOwnButtonContainer}>
              <FileTrigger
                accept={['image/png', 'image/jpeg']}
                allowsMultiple={false}
                onSelect={async (files) => {
                  if (!files) return
                  const mediaFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
                    brand_kit_id: brandKitId,
                  })
                  const image = {
                    id: mediaFiles?.[0]?.id,
                    url: mediaFiles?.[0]?.url,
                    type: mediaFiles?.[0]?.mime_type?.startsWith('video') ? 'video' : 'image',
                  }
                  onSelectImage(image as MediaFile)
                  onNext(image as MediaFile)
                }}
              >
                <div className={Styles.tryYourOwnButtonContent}>
                  <Button
                    variant="secondary"
                    size="xl"
                    aria-label="try your own"
                    frontIcon={Upload5}
                    isDisabled={isUploading || isDisabled}
                  >
                    {isUploading ? (
                      <>
                        Uploading
                        <LoadingEllipses />
                      </>
                    ) : (
                      'Try with your own photo'
                    )}
                  </Button>
                </div>
              </FileTrigger>
            </div>
            <div className={Styles.examplesContainer}>
              <Text variant="secondary" color="var(--dark-40)">
                Examples
              </Text>
              <div className={Styles.examples}>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'product' })}
                  onClick={() => setExampleSelected('product')}
                >
                  Product
                </div>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'person' })}
                  onClick={() => setExampleSelected('person')}
                >
                  Person
                </div>
                <div
                  className={cx(Styles.example, { [Styles.selected]: exampleSelected === 'place' })}
                  onClick={() => setExampleSelected('place')}
                >
                  Place
                </div>
              </div>
            </div>
            <div className={cx(Styles.sourceTag, Styles.imageTag)}>Source Image</div>
          </div>
        </div>
        <div className={Styles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isUploading}>
            Back
          </Button>
          <div className={Styles.footerRightButtons}>
            <Button size="lg" variant="secondary" onPress={() => onNext()} isDisabled={isUploading || isDisabled}>
              Continue without your photo
            </Button>
            <FileTrigger
              accept={['image/png', 'image/jpeg']}
              allowsMultiple={false}
              onSelect={async (files) => {
                if (!files) return
                const mediaFiles = await uploadImageFilesWithoutLoadingToast(Array.from(files), undefined, {
                  brand_kit_id: brandKitId,
                })
                const image = {
                  id: mediaFiles?.[0]?.id,
                  url: mediaFiles?.[0]?.url,
                  type: mediaFiles?.[0]?.mime_type?.startsWith('video') ? 'video' : 'image',
                }
                onSelectImage(image as MediaFile)
                onNext(image as MediaFile)
              }}
            >
              <Button
                variant="primary"
                size="lg"
                aria-label="try with your own photo"
                frontIcon={Upload5}
                isDisabled={isUploading || isDisabled}
              >
                {isUploading ? (
                  <>
                    Uploading
                    <LoadingEllipses />
                  </>
                ) : (
                  'Try with your own photo'
                )}
              </Button>
            </FileTrigger>
          </div>
        </div>
      </div>
      <div className={Styles.transformedImageContainer}>
        <img
          src={exampleGenerated[exampleSelected]}
          alt="demo transformed"
          className={cx(Styles.transformedImage, Styles.visible)}
          onContextMenu={(e) => e?.preventDefault()}
        />
        <div className={cx(Styles.imageTag, Styles.generatedTag)}>Generated Image</div>
      </div>
    </div>
  )
}
